import type {NormalizeForDiff} from '_logics/code-highlight';
import {applyVolatileRules, type CompiledVolatileRule} from '_logics/pghistoryVolatileRules';

export type DiffLine = {
  type: 'add' | 'delete' | 'same';
  content: string;
  beforeContent?: string;
  afterContent?: string;
  beforeLineNum?: number;
  afterLineNum?: number;
  /** 基本正規化では一致するが、実行時I/O状態が異なる行か。 */
  volatileStateChanged?: boolean;
};

export type ComputeLineDiffOptions = {
  /** 実行時に変化する値を差分ハイライトの対象に含める。行の対応付けは変えない。 */
  includeVolatileState?: boolean;
  /**
   * 先頭から連続してこの正規表現に一致する行を、比較では変更として扱わない（表示する原文は変えない）。
   * 分類ルールの「先頭の除外行」（API の `normalized_text`）と同じく、各行に `search` 相当で照合する。
   * 両側の除外行は上から順に対応付け、数が違って余った行は片側だけの変更なし行として返す。
   */
  ignoreLeadingLinePattern?: RegExp;
  /**
   * プロファイルの git 行の揮発値ルール。空でなければ言語の組み込みの揮発値除外より優先する。
   * 言語の正規化は構造的なもの（FANUC の行番号除去など）だけを使い、その結果にルールを当てる。
   * drop 行は連続ブロック内で同内容を対応付け、相手のない行は片側だけの変更なし行として返す。
   */
  volatileRules?: readonly CompiledVolatileRule[];
};

/**
 * 比較では常に一致させる行の比較用の値。本文の行とは一致しない。
 * 先頭の除外行とルールで除外した行は別の値にして、種類が違う余り行どうしを1行に対応付けない。
 */
const IGNORED_LEADING_LINE = '\u0000pghistory-ignored-leading-line\u0000';
const IGNORED_DROPPED_LINE = '\u0000pghistory-ignored-dropped-line\u0000';

/** 先頭から連続して pattern に一致する行数（行末の CR は除いて照合する） */
const countLeadingMatches = (lines: string[], pattern: RegExp | undefined): number => {
  if (!pattern) return 0;
  // g / y フラグの lastIndex に判定が左右されないよう、状態を持たない複製で照合する
  const matcher = new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, ''));
  let count = 0;
  while (count < lines.length && matcher.test(lines[count].replace(/\r$/, ''))) count++;
  return count;
};

const maskLeading = (lines: string[], count: number): string[] =>
  count ? lines.map((line, index) => (index < count ? IGNORED_LEADING_LINE : line)) : lines;

type DiffOperation =
  | {type: 'same'; beforeIndex: number; afterIndex: number}
  | {type: 'delete'; beforeIndex: number}
  | {type: 'add'; afterIndex: number};

const getFrontierValue = (frontier: Map<number, number>, diagonal: number): number =>
  frontier.get(diagonal) ?? 0;

/**
 * Myersの最短編集スクリプトを後ろからたどり、行のインデックス付き操作へ変換する。
 */
const backtrack = (
  trace: Map<number, number>[],
  beforeLines: string[],
  afterLines: string[]
): DiffOperation[] => {
  const result: DiffOperation[] = [];
  let beforeIndex = beforeLines.length;
  let afterIndex = afterLines.length;

  for (let editDistance = trace.length - 1; editDistance > 0; editDistance--) {
    const diagonal = beforeIndex - afterIndex;
    const previousFrontier = trace[editDistance - 1];
    const previousDiagonal =
      diagonal === -editDistance ||
      (diagonal !== editDistance &&
        getFrontierValue(previousFrontier, diagonal - 1) <
          getFrontierValue(previousFrontier, diagonal + 1))
        ? diagonal + 1
        : diagonal - 1;
    const previousBeforeIndex = getFrontierValue(previousFrontier, previousDiagonal);
    const previousAfterIndex = previousBeforeIndex - previousDiagonal;

    while (beforeIndex > previousBeforeIndex && afterIndex > previousAfterIndex) {
      beforeIndex--;
      afterIndex--;
      result.push({type: 'same', beforeIndex, afterIndex});
    }

    if (beforeIndex === previousBeforeIndex) {
      afterIndex--;
      result.push({type: 'add', afterIndex});
    } else {
      beforeIndex--;
      result.push({type: 'delete', beforeIndex});
    }
  }

  while (beforeIndex > 0 && afterIndex > 0) {
    beforeIndex--;
    afterIndex--;
    result.push({type: 'same', beforeIndex, afterIndex});
  }

  while (beforeIndex > 0) {
    beforeIndex--;
    result.push({type: 'delete', beforeIndex});
  }

  while (afterIndex > 0) {
    afterIndex--;
    result.push({type: 'add', afterIndex});
  }

  return result.reverse();
};

/**
 * 2つの行配列をMyers法で比較し、行の対応付けを返す。
 */
const computeMyersOperations = (beforeLines: string[], afterLines: string[]): DiffOperation[] => {
  if (beforeLines.length === 0 && afterLines.length === 0) return [];

  const maxEditDistance = beforeLines.length + afterLines.length;
  let frontier = new Map<number, number>([[1, 0]]);
  const trace: Map<number, number>[] = [];

  for (let editDistance = 0; editDistance <= maxEditDistance; editDistance++) {
    const nextFrontier = new Map<number, number>();

    for (let diagonal = -editDistance; diagonal <= editDistance; diagonal += 2) {
      let beforeIndex: number;

      if (
        diagonal === -editDistance ||
        (diagonal !== editDistance &&
          getFrontierValue(frontier, diagonal - 1) < getFrontierValue(frontier, diagonal + 1))
      ) {
        // 下方向へ進む: after側の行を追加する。
        beforeIndex = getFrontierValue(frontier, diagonal + 1);
      } else {
        // 右方向へ進む: before側の行を削除する。
        beforeIndex = getFrontierValue(frontier, diagonal - 1) + 1;
      }

      let afterIndex = beforeIndex - diagonal;
      while (
        beforeIndex < beforeLines.length &&
        afterIndex < afterLines.length &&
        beforeLines[beforeIndex] === afterLines[afterIndex]
      ) {
        beforeIndex++;
        afterIndex++;
      }

      nextFrontier.set(diagonal, beforeIndex);

      if (beforeIndex >= beforeLines.length && afterIndex >= afterLines.length) {
        trace.push(nextFrontier);
        return backtrack(trace, beforeLines, afterLines);
      }
    }

    trace.push(nextFrontier);
    frontier = nextFrontier;
  }

  return [];
};

const normalizeLines = (
  lines: string[],
  normalizeForDiff?: NormalizeForDiff,
  options?: {includeVolatileState?: boolean}
): string[] => {
  if (!normalizeForDiff) return lines;

  try {
    const normalizedLines = normalizeForDiff(lines, options);
    return normalizedLines.length === lines.length ? normalizedLines : lines;
  } catch {
    return lines;
  }
};

type ComparisonSide = {
  /** 行の対応付けに使う値 */
  comparison: string[];
  /** 比較では常に一致させる行か（先頭の除外行・揮発値ルールで除外した行） */
  ignored: boolean[];
  /** drop 行の内容対応用の値（構造正規化済み、行末 CR 除去）。ルールなしなら null */
  droppedValues: string[] | null;
  /** 「実行時の値」ON のハイライト用の値。OFF なら null */
  volatile: string[] | null;
};

/** 片側の行から、比較用・ハイライト用の値と除外行のフラグを作る */
const comparisonSide = (
  lines: string[],
  normalizeForDiff: NormalizeForDiff | undefined,
  options: ComputeLineDiffOptions,
  leading: number
): ComparisonSide => {
  const rules = options.volatileRules?.length ? options.volatileRules : null;
  if (!rules) {
    return {
      comparison: maskLeading(
        normalizeLines(lines, normalizeForDiff, {includeVolatileState: false}),
        leading
      ),
      ignored: lines.map((_, index) => index < leading),
      droppedValues: null,
      volatile: options.includeVolatileState
        ? maskLeading(
            normalizeLines(lines, normalizeForDiff, {includeVolatileState: true}),
            leading
          )
        : null
    };
  }

  // ルールがあるときは、言語の正規化を構造的なものに限り、揮発値の除外はルールに任せる
  const base = normalizeLines(lines, normalizeForDiff, {includeVolatileState: true});
  const applied = applyVolatileRules(base, rules);
  const droppedValues = base.map(line => line.replace(/\r$/, ''));
  const ignored = applied.map((line, index) => index < leading || line === null);
  return {
    comparison: applied.map((line, index) =>
      index < leading ? IGNORED_LEADING_LINE : line === null ? IGNORED_DROPPED_LINE : line
    ),
    ignored,
    droppedValues,
    // 行末の CR だけの違いはハイライトしない（ルールの適用側も CR を除いて照合している）
    volatile: options.includeVolatileState ? maskLeading(droppedValues, leading) : null
  };
};

/** 共通の両端を外し、内容が全て変わった drop ブロックでは Myers の大きな探索履歴を作らない。 */
const computeDroppedOperations = (before: string[], after: string[]): DiffOperation[] => {
  const result: DiffOperation[] = [];
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) {
    result.push({type: 'same', beforeIndex: start, afterIndex: start});
    start++;
  }
  let beforeEnd = before.length;
  let afterEnd = after.length;
  while (beforeEnd > start && afterEnd > start && before[beforeEnd - 1] === after[afterEnd - 1]) {
    beforeEnd--;
    afterEnd--;
  }
  const beforeMiddle = before.slice(start, beforeEnd);
  const afterMiddle = after.slice(start, afterEnd);
  const beforeContents = new Set(beforeMiddle);
  if (afterMiddle.some(line => beforeContents.has(line))) {
    for (const operation of computeMyersOperations(beforeMiddle, afterMiddle)) {
      if (operation.type === 'same')
        result.push({
          type: 'same',
          beforeIndex: operation.beforeIndex + start,
          afterIndex: operation.afterIndex + start
        });
      else if (operation.type === 'delete')
        result.push({type: 'delete', beforeIndex: operation.beforeIndex + start});
      else result.push({type: 'add', afterIndex: operation.afterIndex + start});
    }
  } else {
    for (let index = start; index < beforeEnd; index++)
      result.push({type: 'delete', beforeIndex: index});
    for (let index = start; index < afterEnd; index++)
      result.push({type: 'add', afterIndex: index});
  }
  while (beforeEnd < before.length) {
    result.push({type: 'same', beforeIndex: beforeEnd++, afterIndex: afterEnd++});
  }
  return result;
};

/** 外側の対応付けを保ち、連続する drop 行だけを内容で再比較する。 */
const alignDroppedOperations = (
  operations: DiffOperation[],
  before: ComparisonSide,
  after: ComparisonSide
): DiffOperation[] => {
  const beforeValues = before.droppedValues;
  const afterValues = after.droppedValues;
  if (!beforeValues || !afterValues) return operations;
  const result: DiffOperation[] = [];
  let beforeIndices: number[] = [];
  let afterIndices: number[] = [];
  const flush = () => {
    if (!beforeIndices.length && !afterIndices.length) return;
    const aligned = computeDroppedOperations(
      beforeIndices.map(index => beforeValues[index]),
      afterIndices.map(index => afterValues[index])
    );
    for (const operation of aligned) {
      if (operation.type === 'same') {
        result.push({
          type: 'same',
          beforeIndex: beforeIndices[operation.beforeIndex],
          afterIndex: afterIndices[operation.afterIndex]
        });
      } else if (operation.type === 'delete') {
        result.push({type: 'delete', beforeIndex: beforeIndices[operation.beforeIndex]});
      } else {
        result.push({type: 'add', afterIndex: afterIndices[operation.afterIndex]});
      }
    }
    beforeIndices = [];
    afterIndices = [];
  };
  for (const operation of operations) {
    const dropped =
      (operation.type === 'add' ||
        before.comparison[operation.beforeIndex] === IGNORED_DROPPED_LINE) &&
      (operation.type === 'delete' ||
        after.comparison[operation.afterIndex] === IGNORED_DROPPED_LINE);
    if (dropped) {
      if (operation.type !== 'add') beforeIndices.push(operation.beforeIndex);
      if (operation.type !== 'delete') afterIndices.push(operation.afterIndex);
    } else {
      flush();
      result.push(operation);
    }
  }
  flush();
  return result;
};

/**
 * 原文の行番号・内容を保持しながら、必要に応じて別の比較用テキストで行差分を計算する。
 * 行の対応付けは、実行時の値を除外した比較用の値（言語の正規化、または `volatileRules`）に対するMyers法で行う。
 * drop 行の連続ブロック内は構造正規化後の内容で再比較し、同じ内容だけを対応付ける。
 * 先頭の除外行と drop 行で対応する相手のない行は
 * 変更ではなく片側だけの `same` 行として返す。「実行時の値」ON では、ルールで除外した片側だけの行と、
 * 原文の違う対応行に `volatileStateChanged` を付ける（先頭の除外行はハイライトしない）。
 */
export const computeLineDiff = (
  before: string,
  after: string,
  normalizeForDiff?: NormalizeForDiff,
  options: ComputeLineDiffOptions = {}
): DiffLine[] => {
  const beforeLines = before ? before.split('\n') : [];
  const afterLines = after ? after.split('\n') : [];
  const leadingBefore = countLeadingMatches(beforeLines, options.ignoreLeadingLinePattern);
  const leadingAfter = countLeadingMatches(afterLines, options.ignoreLeadingLinePattern);
  const beforeSide = comparisonSide(beforeLines, normalizeForDiff, options, leadingBefore);
  const afterSide = comparisonSide(afterLines, normalizeForDiff, options, leadingAfter);
  const hasRules = (options.volatileRules?.length ?? 0) > 0;

  // 比較用の値と表示する原文が異なりうるときは、左右それぞれの原文を返す
  const preserveSourceContents =
    normalizeForDiff !== undefined || options.ignoreLeadingLinePattern !== undefined || hasRules;
  const includeVolatileComparison =
    normalizeForDiff !== undefined || options.includeVolatileState || hasRules;
  // 片側だけの除外行のうち、ルールで除外した行は「実行時の値」ON でハイライトする
  const oneSidedVolatile = (
    index: number,
    leading: number
  ): Pick<DiffLine, 'volatileStateChanged'> =>
    options.includeVolatileState && index >= leading ? {volatileStateChanged: true} : {};

  const operations = alignDroppedOperations(
    computeMyersOperations(beforeSide.comparison, afterSide.comparison),
    beforeSide,
    afterSide
  );
  return operations.map(operation => {
    if (operation.type === 'same') {
      const beforeContent = beforeLines[operation.beforeIndex];
      const afterContent = afterLines[operation.afterIndex];
      const diffLine: DiffLine = {
        type: 'same',
        content: afterContent,
        beforeLineNum: operation.beforeIndex + 1,
        afterLineNum: operation.afterIndex + 1,
        ...(preserveSourceContents ? {beforeContent, afterContent} : {})
      };

      if (includeVolatileComparison) {
        diffLine.volatileStateChanged =
          beforeSide.volatile !== null &&
          afterSide.volatile !== null &&
          beforeSide.volatile[operation.beforeIndex] !== afterSide.volatile[operation.afterIndex];
      }

      return diffLine;
    }

    if (operation.type === 'delete') {
      const content = beforeLines[operation.beforeIndex];
      const ignored = beforeSide.ignored[operation.beforeIndex];
      return {
        // 対応する相手のない除外行は、変更ではなく片側だけの行として返す
        type: ignored ? 'same' : 'delete',
        content,
        beforeLineNum: operation.beforeIndex + 1,
        ...(preserveSourceContents ? {beforeContent: content} : {}),
        ...(ignored ? oneSidedVolatile(operation.beforeIndex, leadingBefore) : {})
      } satisfies DiffLine;
    }

    const content = afterLines[operation.afterIndex];
    const ignored = afterSide.ignored[operation.afterIndex];
    return {
      type: ignored ? 'same' : 'add',
      content,
      afterLineNum: operation.afterIndex + 1,
      ...(preserveSourceContents ? {afterContent: content} : {}),
      ...(ignored ? oneSidedVolatile(operation.afterIndex, leadingAfter) : {})
    } satisfies DiffLine;
  });
};
